import { Injectable, Inject, Optional, inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { REQUEST_META_DATA } from '@seech/shared-ng';
import { ToastService } from '../services/toast.service';
import {
  DEFAULT_HTTP_FEEDBACK_CONFIG,
  HTTP_FEEDBACK_CONFIG,
  HTTP_FEEDBACK_CONTEXT,
  HttpFeedbackConfig,
  HttpFeedbackPresenter,
  HttpFeedbackPresenterPayload,
  HttpFeedbackRequestContext,
  ResolvedHttpFeedbackConfig,
} from './http-feedback.config';

@Injectable()
export class HttpFeedbackInterceptor implements HttpInterceptor {
  private readonly toastService = inject(ToastService);
  private readonly globalConfig: ResolvedHttpFeedbackConfig;
  private readonly defaultPresenter: HttpFeedbackPresenter;
  private readonly feedbackContextToken: HttpContextToken<boolean> | null;

  constructor(
    @Optional()
    @Inject(HTTP_FEEDBACK_CONFIG)
    config?: HttpFeedbackConfig | ResolvedHttpFeedbackConfig | null
  ) {
    const requestMetaTokens =
      REQUEST_META_DATA as { [key: string]: HttpContextToken<boolean> };
    this.feedbackContextToken = requestMetaTokens?.['FEEDBACK'] ?? null;
    this.globalConfig = this.mergeConfig(
      DEFAULT_HTTP_FEEDBACK_CONFIG,
      config ?? undefined
    );
    this.defaultPresenter = (payload, meta) => {
      const toastConfig =
        meta.context?.toastOptions?.[payload.severity] ??
        meta.config.toastOptions?.[payload.severity];

      switch (payload.severity) {
        case 'success':
          this.toastService.success(payload.message, toastConfig);
          break;
        case 'warning':
          this.toastService.warning(payload.message, toastConfig);
          break;
        case 'error':
          this.toastService.error(payload.message, toastConfig);
          break;
        default:
          this.toastService.info(payload.message, toastConfig);
      }
    };
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      req.context.get(REQUEST_META_DATA.ALL) === true ||
      (this.feedbackContextToken
        ? req.context.get(this.feedbackContextToken) === true
        : false)
    ) {
      return next.handle(req);
    }

    const perRequestContext = req.context.get(HTTP_FEEDBACK_CONTEXT);

    if (perRequestContext?.skip === true) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.handleResponse(event, perRequestContext, false);
        }
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          this.handleResponse(error, perRequestContext, true);
        }
        return throwError(() => error);
      })
    );
  }

  private handleResponse(
    response: HttpResponse<any> | HttpErrorResponse,
    override: HttpFeedbackRequestContext | null,
    isError: boolean
  ) {
    const effectiveConfig = this.mergeConfig(
      this.globalConfig,
      this.extractConfigOverrides(override)
    );

    if (!effectiveConfig.enabled) {
      return;
    }

    if (isError && !effectiveConfig.handleError) {
      return;
    }

    if (!isError && !effectiveConfig.handleSuccess) {
      return;
    }

    const payload = this.createPayload(response, effectiveConfig, override, isError);

    if (!payload) {
      return;
    }

    const presenter = override?.presenter ?? effectiveConfig.presenter ?? this.defaultPresenter;
    presenter(payload, { config: effectiveConfig, context: override });
  }

  private createPayload(
    response: HttpResponse<any> | HttpErrorResponse,
    config: ResolvedHttpFeedbackConfig,
    override: HttpFeedbackRequestContext | null,
    isError: boolean
  ): HttpFeedbackPresenterPayload | null {
    const status = override?.status ?? this.extractStatus(response, config);
    const message =
      override?.message ?? this.extractMessageFromBody(response, config);

    if (!message) {
      return null;
    }

    const severity =
      override?.severity ??
      config.severityResolver({ status, response, isError });

    return {
      message,
      status,
      severity,
      response,
      isError,
    };
  }

  private extractStatus(
    response: HttpResponse<any> | HttpErrorResponse,
    config: ResolvedHttpFeedbackConfig
  ): number | undefined {
    const headerValue = this.extractHeaderValue(
      response,
      config.headerStatusCodeKey
    );

    if (headerValue && !Number.isNaN(Number(headerValue))) {
      return Number(headerValue);
    }

    const candidate = this.normalizePayload(this.getBodyCandidate(response));
    const statusValue =
      candidate && typeof candidate === 'object'
        ? this.extractFromPaths(candidate, config.bodyStatusCodePaths)
        : undefined;

    if (
      typeof statusValue === 'number' &&
      Number.isFinite(statusValue as number)
    ) {
      return statusValue as number;
    }

    if (
      typeof statusValue === 'string' &&
      !Number.isNaN(Number(statusValue as string))
    ) {
      return Number(statusValue as string);
    }

    return response.status || undefined;
  }

  private extractHeaderValue(
    response: HttpResponse<any> | HttpErrorResponse,
    key?: string
  ): string | null {
    if (!key) {
      return null;
    }

    const headers = response.headers;
    if (!headers) {
      return null;
    }

    const value = this.getHeaderValueCaseInsensitive(headers, key);
    return this.toDisplayString(value);
  }

  private extractMessageFromBody(
    response: HttpResponse<any> | HttpErrorResponse,
    config: ResolvedHttpFeedbackConfig
  ): string | null {
    const candidate = this.normalizePayload(this.getBodyCandidate(response));

    if (typeof candidate === 'string') {
      return candidate;
    }

    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    const value = this.extractFromPaths(candidate, config.bodyMessagePaths);
    return this.toDisplayString(value);
  }

  private extractFromPaths(source: any, paths: string[][]): any {
    for (const path of paths) {
      let current = source;
      let found = true;
      for (const segment of path) {
        if (
          current &&
          typeof current === 'object' &&
          segment in current
        ) {
          current = current[segment];
        } else {
          found = false;
          break;
        }
      }

      if (found && current !== undefined && current !== null) {
        return current;
      }
    }

    return undefined;
  }

  private getBodyCandidate(
    response: HttpResponse<any> | HttpErrorResponse
  ): any {
    if (response instanceof HttpErrorResponse) {
      return response.error ?? response.message;
    }

    return response.body;
  }

  private normalizePayload(payload: any): any {
    if (payload === null || payload === undefined) {
      return undefined;
    }

    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (!trimmed) {
        return undefined;
      }

      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    }

    if (payload instanceof ArrayBuffer || payload instanceof Blob) {
      return undefined;
    }

    return payload;
  }

  private toDisplayString(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return null;
  }

  private mergeConfig(
    base: ResolvedHttpFeedbackConfig,
    override?: Partial<HttpFeedbackConfig> | null
  ): ResolvedHttpFeedbackConfig {
    if (!override) {
      return {
        ...base,
        toastOptions: {
          ...base.toastOptions,
        },
      };
    }

    return {
      ...base,
      ...override,
      enabled: override.enabled ?? base.enabled,
      handleSuccess: override.handleSuccess ?? base.handleSuccess,
      handleError: override.handleError ?? base.handleError,
      headerStatusCodeKey:
        override.headerStatusCodeKey ?? base.headerStatusCodeKey,
      bodyMessagePaths: override.bodyMessagePaths ?? base.bodyMessagePaths,
      bodyStatusCodePaths:
        override.bodyStatusCodePaths ?? base.bodyStatusCodePaths,
      toastOptions: {
        ...base.toastOptions,
        ...(override.toastOptions ?? {}),
      },
      severityResolver: override.severityResolver ?? base.severityResolver,
      presenter: override.presenter ?? base.presenter,
    };
  }

  private extractConfigOverrides(
    override?: HttpFeedbackRequestContext | null
  ): Partial<HttpFeedbackConfig> | undefined {
    if (!override) {
      return undefined;
    }

    const { skip, message, severity, status, ...rest } = override;
    return rest;
  }

  private getHeaderValueCaseInsensitive(
    headers: HttpHeaders,
    key: string
  ): string | null {
    if (!headers || !key) {
      return null;
    }

    const direct = headers.get(key);
    if (direct) {
      return direct;
    }

    const normalizedKey = key.toLowerCase();
    for (const headerKey of headers.keys()) {
      if (headerKey.toLowerCase() === normalizedKey) {
        return headers.get(headerKey);
      }
    }

    return null;
  }

}
